import BoxKit from "@/shared/components/kit/box";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import React from "react";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import NorthWestOutlinedIcon from "@mui/icons-material/NorthWestOutlined";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from "chart.js";
import { Bar } from "react-chartjs-2";
import moment from "jalali-moment";
import CardBoxLayout from "../../layout/cardBoxLayout";
import { numberSeparator, toPersianNumber, } from "@/hooks/useThousandsSeparator";
import { useNavigate } from "react-router";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
/**
 * WalletReports
 *
 * نمایش نمودار تراکنش‌های کیف پول کاربر شامل واریز و برداشت.
 *
 * - دریافت داده‌ها از props `data`.
 * - تفکیک مقادیر واریز و برداشت.
 * - استخراج تاریخ تراکنش‌ها به فرمت شمسی (Jalali) و حذف تکراری‌ها.
 * - محاسبه حداقل و حداکثر مقادیر برای محور Y نمودار.
 * - استفاده از Chart.js و react-chartjs-2 برای رسم نمودار میله‌ای.
 * - نمایش tooltip سفارشی شامل نوع تراکنش و مبلغ به ریال با جداکننده هزارگان.
 * - دکمه برای رفتن به صفحه گزارش کامل کیف پول.
 *
 * @param {Object} props
 * @param {any} props.data داده‌های تراکنش‌ها
 *
 * @returns JSX.Element
 */

const WalletReports = ({ data }: { data: any }) => {
  const navigate = useNavigate();
  const depositAmounts = React.useMemo(() => {
    if (!data?.data?.transfers || data?.data?.transfers.length === 0) {
      return [];
    }
    return data?.data?.transfers
      .filter((transfer: any) => transfer.amount > 0)
      .map((transfer: any) => transfer.amount);
  }, [data?.data?.transfers]);

  const withdrawAmounts = React.useMemo(() => {
    if (!data?.data?.transfers || data?.data?.transfers.length === 0) {
      return [];
    }
    return data.data.transfers
      .filter((transfer: any) => transfer.amount < 0)
      .map((transfer: any) => transfer.amount);
  }, [data?.data?.transfers]);

  const transferDates = React.useMemo(() => {
    if (!data?.data?.transfers || data?.data?.transfers.length === 0) {
      return [];
    }

    const dates = data.data.transfers.map((transfer: any) => {
      const rawDate = transfer.created_at;
      return moment(rawDate).locale("fa").format("jYYYY/jMM/jDD");
    });

    return Array.from(new Set(dates));
  }, [data?.data?.transfers]);

  const transferPay = React.useMemo(() => {
    if (!data?.data?.transfers || data?.data?.transfers.length === 0) {
      return [];
    }

    return data.data.transfers.map((transfer: any) => {
      const rawDate = transfer;
      return;
    });
  }, [data?.data?.transfers]);

  const allAmounts = React.useMemo(() => {
    if (!data?.data?.transfers) return [];
    return data.data.transfers.map((t: any) => Math.abs(t.amount));
  }, [data?.data?.transfers]);

  const { minAmount, maxAmount } = React.useMemo(() => {
    if (!data?.data?.transfers?.length) {
      return { minAmount: 0, maxAmount: 10000 };
    }

    const amounts = data.data.transfers.map((t: any) => t.amount);

    const min = Math.min(...amounts);
    const max = Math.max(...amounts);

    const roundedMax = Math.ceil(max / 10000) * 10000;
    const roundedMin = Math.floor(min / 10000) * 10000;

    return { minAmount: roundedMin, maxAmount: roundedMax };
  }, [data?.data?.transfers]);

  const chartData = {
    labels: transferDates.map((date: any) => toPersianNumber(date)),
    datasets: [
      {
        label: "واریز",
        data: depositAmounts,
        backgroundColor: "#7f5af0",
        borderColor: "#7f5af0",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
        stack: "combined",
        categoryPercentage: 0.1,
        barPercentage: 0.6,
      },
      {
        label: "برداشت",
        data: withdrawAmounts,
        backgroundColor: "#666666",
        borderColor: "#666666",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
        stack: "combined",
        categoryPercentage: 0.1,
        barPercentage: 0.6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: "#fff",
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#333",
        borderWidth: 1,
        callbacks: {
          label: function (context: any) {
            const value = context.raw;
            const absVal = Math.abs(value);
            const type = value >= 0 ? "واریز" : "برداشت";
            return `${type}: ${numberSeparator(absVal)} ریال`;
          },
        },
      },
    },
    scales: {
      x: {
        categoryPercentage: 0.3,
        barPercentage: 0.6,
        ticks: {
          maxTicksLimit: 5,
          color: "#fff",
          font: {
            size: 12,
          },
        },
        grid: {
          color: "#333",
        },
      },
      y: {
        min: minAmount,
        max: maxAmount,
        ticks: {
          stepSize: 500000,
          maxTicksLimit: 5,
          color: "#fff",
          font: {
            size: 12,
          },
          callback: function (value: any) {
            return `${numberSeparator(value)} ریال`;
          },
        },
        grid: {
          color: "#333",
        },
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
      },
    },
  };

  return (
    <CardBoxLayout
      sx={{
        background: "linear-gradient(153.86deg, rgba(0, 0, 0, 0.4) -11.84%, rgba(37, 37, 37, 0.370427) 14.24%, rgba(78, 78, 78, 0.340413) 40.7%, rgba(0, 0, 0, 0.237218) 75.66%, rgba(0, 0, 0, 0.12) 106.2%)",
        padding: "10px 30px",
      }}
    >
      <StackKit direction={"row"} justifyContent={"space-between"}>
        <StackKit direction={"row"} alignItems={"center"} gap={1}>
          <BarChartOutlinedIcon style={{ color: "#fff" }} />
          <TypoGraphyKit>گزارش کیف پول</TypoGraphyKit>
        </StackKit>
        <BoxKit sx={{ display: "flex", gap: 1, bgcolor: "#333333", width: "30px", height: "30px", borderRadius: "50%", alignItems: "center", justifyContent: "center", }}>
          <NorthWestOutlinedIcon style={{ color: "#fff", cursor: "pointer" }} onClick={() => navigate("/wallet-reports")} />
        </BoxKit>
      </StackKit>
      <div style={{ height: "387px", width: "100%" }}>
        <Bar data={chartData} options={options} />
      </div>
    </CardBoxLayout>
  );
};

export default WalletReports;
